<script setup>
import { ref, onMounted, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import FunnelsHeader from './FunnelsHeader.vue';
import Modal from '../../../../components/Modal.vue';
import FunnelAPI from '../../../../api/funnel';
import FunnelForm from './FunnelForm.vue';
import { emitter } from 'shared/helpers/mitt';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import KanbanAPI from '../../../../api/kanban';
import KanbanMetrics from '../../settings/reports/components/KanbanMetrics.vue';
import FunnelsGlobalAiSettings from './FunnelsGlobalAiSettings.vue';

// Prop para detectar modo de criação
const props = defineProps({
  createMode: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['switch-view']);
const { t, locale } = useI18n();
const router = useRouter();
const loading = ref(false);
const funnels = ref([]);

// Estado para os modais
const showDeleteModal = ref(false);
const funnelToDelete = ref(null);

// Estado para edição inline
const isEditingMode = ref(false);
const funnelBeingEdited = ref(null);

// Estado para criação de novo funil
const isCreatingMode = ref(false);

// Estado para controlar salvamento externo do FunnelForm
const externalSaveTrigger = ref(false);

// Estado para métricas
const showMetricsModal = ref(false);
const selectedFunnelForMetrics = ref(null);
const metricsData = ref({});
const metricsLoading = ref(false);

const resolveFunnelApiErrorMessage = (error, fallbackMessage) => {
  const nameErrors = error?.response?.data?.errors?.name;
  if (Array.isArray(nameErrors) && nameErrors.length > 0) {
    return t('KANBAN.FUNNELS.FORM.ERRORS.NAME_TAKEN');
  }

  return error?.response?.data?.error || fallbackMessage;
};

const refreshFunnelData = async () => {
  loading.value = true;
  try {
    const response = await FunnelAPI.get();
    const apiFunnels = response.data || [];

    funnels.value = apiFunnels.map(funnel => {
      // Garantir que os estágios tenham sempre posição definida
      const stages = Object.entries(funnel.stages || {}).reduce(
        (acc, [id, stage]) => ({
          ...acc,
          [id]: {
            ...stage,
            id,
            position: parseInt(stage.position, 10) || 0,
            message_templates: stage.message_templates || [],
          },
        }),
        {}
      );
      return {
        ...funnel,
        stages,
      };
    });
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: 'Erro ao carregar dados dos funis.',
      action: { type: 'error' },
    });
  } finally {
    loading.value = false;
  }
};

const confirmDelete = funnel => {
  funnelToDelete.value = funnel;
  showDeleteModal.value = true;
};

const handleDelete = async () => {
  try {
    loading.value = true;
    const deletedId = funnelToDelete.value.id;
    await FunnelAPI.delete(deletedId);
    await refreshFunnelData(); // Atualiza a partir da API

    if (selectedFunnelForMetrics.value?.id === deletedId) {
      showMetricsModal.value = false;
      selectedFunnelForMetrics.value = null;
    }

    emitter.emit('newToastMessage', {
      message: 'Funil excluído com sucesso',
      action: { type: 'success' },
    });

    showDeleteModal.value = false;
    funnelToDelete.value = null;
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: error.response?.data?.error || 'Erro ao excluir funil',
      action: { type: 'error' },
    });
  } finally {
    loading.value = false;
  }
};

const startEdit = funnel => {
  funnelBeingEdited.value = {
    ...funnel,
    id: String(funnel.id),
    stages: Object.entries(funnel.stages || {}).reduce(
      (acc, [key, stage]) => ({
        ...acc,
        [String(key)]: {
          ...stage,
          id: String(stage.id || key),
        },
      }),
      {}
    ),
    settings: funnel.settings || {}, // Preserva todas as configurações
    agents: funnel.settings?.agents || [], // Carrega os agentes do settings
  };

  isEditingMode.value = true;
  isCreatingMode.value = false;

  // Adicionar parâmetros 'funnel' e 'edit' na URL
  const currentQuery = { ...router.currentRoute.value.query };
  currentQuery.funnel = funnel.id;
  currentQuery.edit = true;
  router.replace({ query: currentQuery });
};

const startCreate = () => {
  funnelBeingEdited.value = null;
  isEditingMode.value = false;
  isCreatingMode.value = true;

  // Remover parâmetro 'funnel' da URL quando criando novo funil
  const currentQuery = { ...router.currentRoute.value.query };
  delete currentQuery.funnel;
  router.replace({ query: currentQuery });
};

// Abre o FunnelForm quando o pai (KanbanView) navega com createMode (ex.: "Novo funil" no header)
watch(
  () => props.createMode,
  newValue => {
    if (newValue) {
      startCreate();
    }
  },
  { immediate: true }
);

// Inicia criação com dados de template
const startCreateFromTemplate = templateData => {
  funnelBeingEdited.value = null;
  isEditingMode.value = false;
  isCreatingMode.value = true;
  // Guarda template no ref do formulário após mount via prop
  initialFormData.value = templateData;

  // Remover parâmetro 'funnel' da URL quando criando novo funil
  const currentQuery = { ...router.currentRoute.value.query };
  delete currentQuery.funnel;
  router.replace({ query: currentQuery });
};

// Estado para metadados do funil sendo editado
const funnelMetadata = ref({
  updated_at: null,
  name: '',
});

const handleMetadataUpdated = metadata => {
  funnelMetadata.value = metadata;
};

const handleEdit = async updatedFunnel => {
  try {
    // Apenas atualiza a lista de funis e fecha o modo de edição/criação
    // O formulário já fez o update/create na API
    await refreshFunnelData();

    const message = isCreatingMode.value
      ? 'Funil criado com sucesso'
      : 'Funil atualizado com sucesso';
    emitter.emit('newToastMessage', {
      message,
      action: { type: 'success' },
    });

    isEditingMode.value = false;
    isCreatingMode.value = false;
    funnelBeingEdited.value = null;
    funnelMetadata.value = { updated_at: null, name: '' };
    externalSaveTrigger.value = false; // Reset the external save trigger

    // Remover parâmetros 'funnel' e 'edit' da URL quando sair do modo de edição/criação
    const currentQuery = { ...router.currentRoute.value.query };
    delete currentQuery.funnel;
    delete currentQuery.edit;
    router.replace({ query: currentQuery });
  } catch (error) {
    const message = isCreatingMode.value
      ? 'Erro ao criar funil'
      : 'Erro ao atualizar a lista de funis';
    emitter.emit('newToastMessage', {
      message,
      action: { type: 'error' },
    });
  }
};

const duplicateFunnel = async funnel => {
  try {
    loading.value = true;
    const timestamp = Date.now();

    // Gera novos IDs para as etapas
    const newStages = Object.entries(funnel.stages).reduce(
      (acc, [_, stage]) => {
        const newId = `stage_${timestamp}_${stage.position}`;
        return {
          ...acc,
          [newId]: {
            ...stage,
            id: newId,
            message_templates: stage.message_templates?.map(template => ({
              ...template,
              id: `template_${timestamp}_${template.id}`,
              stage_id: newId,
            })),
          },
        };
      },
      {}
    );

    const newFunnel = {
      name: `${funnel.name} (cópia)`,
      description: funnel.description,
      active: funnel.active,
      stages: newStages,
      settings: {
        ...funnel.settings,
        agents: funnel.settings?.agents || [],
      },
    };

    await FunnelAPI.create(newFunnel);
    await refreshFunnelData(); // Atualiza a partir da API
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: resolveFunnelApiErrorMessage(error, 'Erro ao duplicar funil'),
      action: { type: 'error' },
    });
  } finally {
    loading.value = false;
  }
};

const defaultCurrency = computed(() => {
  const i18nLocale = locale.value || 'pt-BR';
  const currencyMap = {
    'pt-BR': { code: 'BRL', locale: 'pt-BR', symbol: 'R$' },
    pt_BR: { code: 'BRL', locale: 'pt-BR', symbol: 'R$' },
    'en-US': { code: 'USD', locale: 'en-US', symbol: '$' },
    en_US: { code: 'USD', locale: 'en-US', symbol: '$' },
    en: { code: 'USD', locale: 'en-US', symbol: '$' },
    'de-DE': { code: 'EUR', locale: 'de-DE', symbol: '€' },
    de_DE: { code: 'EUR', locale: 'de-DE', symbol: '€' },
    de: { code: 'EUR', locale: 'de-DE', symbol: '€' },
    'es-ES': { code: 'EUR', locale: 'es-ES', symbol: '€' },
    es_ES: { code: 'EUR', locale: 'es-ES', symbol: '€' },
    es: { code: 'EUR', locale: 'es-ES', symbol: '€' },
    'fr-FR': { code: 'EUR', locale: 'fr-FR', symbol: '€' },
    fr_FR: { code: 'EUR', locale: 'fr-FR', symbol: '€' },
    fr: { code: 'EUR', locale: 'fr-FR', symbol: '€' },
    'pt-PT': { code: 'EUR', locale: 'pt-PT', symbol: '€' },
    pt_PT: { code: 'EUR', locale: 'pt-PT', symbol: '€' },
  };

  return currencyMap[i18nLocale] || currencyMap['pt-BR'];
});

const formatCurrency = value => {
  if (value === null || value === undefined || isNaN(value)) {
    return new Intl.NumberFormat(defaultCurrency.value.locale, {
      style: 'currency',
      currency: defaultCurrency.value.code,
    }).format(0);
  }

  const currency = defaultCurrency.value;
  const absValue = Math.abs(value);
  if (absValue >= 1000000) {
    const millions = absValue / 1000000;
    const formatted =
      millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1);
    return `${currency.symbol} ${formatted}M`;
  }

  try {
    return new Intl.NumberFormat(currency.locale, {
      style: 'currency',
      currency: currency.code,
    }).format(value);
  } catch (error) {
    return `${currency.symbol} ${value.toFixed(2)}`;
  }
};

const openFunnelEditFromMetrics = () => {
  if (!selectedFunnelForMetrics.value) return;
  const funnel = selectedFunnelForMetrics.value;
  showMetricsModal.value = false;
  startEdit(funnel);
};

const showMetrics = async funnel => {
  selectedFunnelForMetrics.value = funnel;
  showMetricsModal.value = true;
  metricsLoading.value = true;

  try {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    const params = {
      funnel_id: funnel.id,
      from: thirtyDaysAgo.getTime(),
      to: today.getTime(),
    };

    const response = await KanbanAPI.getReports(params);
    metricsData.value = response.data;
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: 'Erro ao carregar métricas do funil.',
      action: { type: 'error' },
    });
  } finally {
    metricsLoading.value = false;
  }
};

// Função para ordenar as etapas de um funil por posição
const getSortedStages = stages => {
  if (!stages) return [];

  // Converter objeto de etapas em array
  const stagesArray = Object.entries(stages)
    .map(([id, stage]) => ({
      id,
      ...stage,
      position: parseInt(stage.position, 10) || 0,
    }))
    .sort((a, b) => a.position - b.position);

  return stagesArray;
};

// Mesma regra que KanbanColumn: contar regras habilitadas em stage_automations por etapa
const countEnabledAutomationsInStage = stage => {
  const list = stage?.stage_automations;
  if (!Array.isArray(list)) return 0;
  return list.filter(rule => rule?.enabled !== false).length;
};

const funnelActiveAutomationsCountById = computed(() => {
  const map = {};
  for (const funnel of funnels.value) {
    if (!funnel?.stages) {
      map[funnel.id] = 0;
      continue;
    }
    let total = 0;
    for (const stage of Object.values(funnel.stages)) {
      total += countEnabledAutomationsInStage(stage);
    }
    map[funnel.id] = total;
  }
  return map;
});

// Funções para controlar o FunnelForm através do header
const handleSaveFromHeader = () => {
  // Verifica se estamos em modo de edição ou criação
  if (!isEditingMode.value && !isCreatingMode.value) {
    console.warn('Tentativa de salvar sem estar em modo de edição ou criação');
    return;
  }

  // Ativa o trigger de salvamento externo
  externalSaveTrigger.value = true;
};

const handleDiscardFromHeader = () => {
  isEditingMode.value = false;
  isCreatingMode.value = false;
  funnelBeingEdited.value = null;
  funnelMetadata.value = { updated_at: null, name: '' };

  // Remover parâmetros 'funnel' e 'edit' da URL quando descartar mudanças
  const currentQuery = { ...router.currentRoute.value.query };
  delete currentQuery.funnel;
  delete currentQuery.edit;
  router.replace({ query: currentQuery });
};

const handleCloseForm = () => {
  isEditingMode.value = false;
  isCreatingMode.value = false;
  funnelBeingEdited.value = null;
  funnelMetadata.value = { updated_at: null, name: '' };

  // Remover parâmetros 'funnel' e 'edit' da URL quando fechar formulário
  const currentQuery = { ...router.currentRoute.value.query };
  delete currentQuery.funnel;
  delete currentQuery.edit;
  router.replace({ query: currentQuery });
};

const handleBack = () => {
  isEditingMode.value = false;
  isCreatingMode.value = false;
  funnelBeingEdited.value = null;
  funnelMetadata.value = { updated_at: null, name: '' };

  // Remover parâmetros 'funnel' e 'edit' da URL quando voltar
  const currentQuery = { ...router.currentRoute.value.query };
  delete currentQuery.funnel;
  delete currentQuery.edit;
  router.replace({ query: currentQuery });
};

// Refs para controlar o FunnelForm
const funnelFormRef = ref(null);
const initialFormData = ref(null);

// SiteUp: regra GLOBAL de auto-criação por IA — vive na conta, não no funil.
const showGlobalAiSettings = ref(false);

onMounted(async () => {
  await refreshFunnelData(); // Busca inicial a partir da API

  // Verificar se há parâmetros 'funnel' e 'edit' na URL para abrir em modo de edição
  const currentQuery = router.currentRoute.value.query;
  if (currentQuery.funnel && currentQuery.edit) {
    const funnelId = currentQuery.funnel;
    const funnel = funnels.value.find(f => f.id == funnelId);
    if (funnel) {
      startEdit(funnel);
    }
  }
});
</script>

<template>
  <div class="flex flex-col h-full bg-n-solid-1 p-4">
    <FunnelsHeader
      :editing-funnel="isEditingMode ? funnelBeingEdited : null"
      :funnel-metadata="funnelMetadata"
      :is-creating="isCreatingMode"
      @switch-view="view => emit('switch-view', view)"
      @back="handleBack"
      @funnel-created="refreshFunnelData"
      @save-funnel="handleSaveFromHeader"
      @discard-changes="handleDiscardFromHeader"
      @create-new-funnel="startCreate"
      @create-from-template="startCreateFromTemplate"
      @open-global-ai="showGlobalAiSettings = true"
    />

    <FunnelsGlobalAiSettings
      :show="showGlobalAiSettings"
      :funnels="funnels"
      @close="showGlobalAiSettings = false"
      @saved="refreshFunnelData"
    />

    <div class="funnels-content flex flex-1 min-h-0 flex-col overflow-y-auto">
      <!-- Renderiza FunnelForm quando estiver editando ou criando -->
      <FunnelForm
        v-if="isEditingMode || isCreatingMode"
        ref="funnelFormRef"
        :is-editing="isEditingMode"
        :funnel-id="isEditingMode ? funnelBeingEdited.id : null"
        :initial-data="!isEditingMode ? initialFormData : null"
        :external-save="externalSaveTrigger"
        @saved="handleEdit"
        @metadata-updated="handleMetadataUpdated"
        @close="handleCloseForm"
      />

      <!-- Renderiza lista de funis quando NÃO estiver editando -->
      <template v-else>
        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center items-center py-12">
          <span class="loading-spinner" />
        </div>

        <!-- Empty State -->
        <div
          v-else-if="!funnels.length"
          class="flex flex-col items-center justify-center py-12 text-n-slate-11"
        >
          <span class="i-ri-kanban-view size-12 mb-4" />
          <p class="text-lg">Nenhum funil encontrado</p>
          <p class="text-sm">
            Crie um novo funil para começar a organizar seus itens
          </p>
        </div>

        <!-- Funnels List -->
        <div
          v-else
          class="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
        >
          <div
            v-for="funnel in funnels"
            :key="funnel.id"
            class="funnel-card p-4 border border-n-weak rounded-xl hover:bg-n-alpha-2 relative cursor-pointer"
            @click="showMetrics(funnel)"
          >
            <!-- Botões de Ação -->
            <div class="absolute top-4 right-4 flex items-center gap-1">
              <Button
                variant="ghost"
                color="slate"
                size="sm"
                @click.stop="startEdit(funnel)"
              >
                <template #icon>
                  <span class="i-ri-pencil-line size-4" />
                </template>
              </Button>
              <Button
                variant="ghost"
                color="slate"
                size="sm"
                @click.stop="duplicateFunnel(funnel)"
              >
                <template #icon>
                  <span class="i-ri-file-copy-line size-4" />
                </template>
              </Button>
              <Button
                variant="ghost"
                color="ruby"
                size="sm"
                @click.stop="confirmDelete(funnel)"
              >
                <template #icon>
                  <span class="i-ri-delete-bin-line size-4" />
                </template>
              </Button>
            </div>

            <div class="flex flex-col gap-4">
              <!-- Informações básicas -->
              <div class="flex items-center gap-2">
                <span class="text-xs text-n-slate-10 select-all"
                  >#{{ funnel.id }}</span
                >
                <h3 class="text-lg font-extrabold">{{ funnel.name }}</h3>
                <span
                  class="text-xs px-2 py-0.5 rounded-full"
                  :class="[
                    funnel.active
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                  ]"
                >
                  {{
                    funnel.active
                      ? t('KANBAN.FUNNELS.CARD.ACTIVE')
                      : t('KANBAN.FUNNELS.CARD.INACTIVE')
                  }}
                </span>
              </div>

              <!-- Descrição -->
              <p
                v-if="funnel.description"
                class="text-sm text-n-slate-11 line-clamp-2"
              >
                {{ funnel.description }}
              </p>

              <!-- Data de criação -->
              <p class="text-xs text-n-slate-11">
                {{ t('KANBAN.FUNNELS.CARD.CREATED_AT') }}:
                {{ new Date(funnel.created_at).toLocaleDateString() }}
              </p>

              <!-- Etapas -->
              <div class="flex flex-col gap-2">
                <p
                  class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
                >
                  {{ t('KANBAN.FUNNELS.CARD.STAGES_LABEL') }}
                </p>
                <div class="flex flex-wrap gap-2">
                  <span
                    v-for="stage in getSortedStages(funnel.stages)"
                    :key="stage.id"
                    class="px-2 py-1 text-xs font-medium rounded-full flex items-center gap-1 group relative"
                    :style="{
                      backgroundColor: `${stage.color}20`,
                      color: stage.color,
                    }"
                  >
                    {{ stage.name }}
                    <!-- Contagem de itens no estágio -->
                    <span
                      v-if="stage.items_count != null && stage.items_count > 0"
                      class="ml-1 px-1.5 text-[10px] rounded-full font-semibold opacity-90"
                      :style="{ backgroundColor: `${stage.color}40` }"
                    >
                      {{ stage.items_count }}
                    </span>
                    <!-- Badge de templates se houver -->
                    <span
                      v-if="stage.message_templates?.length"
                      class="ml-1 px-1.5 text-[10px] rounded-full bg-n-alpha-2"
                    >
                      {{ stage.message_templates.length }}
                    </span>

                    <!-- Tooltip com templates -->
                    <div
                      v-if="stage.message_templates?.length"
                      class="absolute hidden group-hover:block bg-n-solid-2 shadow-lg rounded-xl p-3 z-10 min-w-[250px] top-full left-0 mt-1"
                    >
                      <div class="flex items-center gap-2 mb-2">
                        <span class="i-ri-chat-1-line size-4 text-woot-500" />
                        <p class="text-xs font-medium">Templates de Mensagem</p>
                      </div>
                      <div class="space-y-2">
                        <div
                          v-for="template in stage.message_templates"
                          :key="template.id"
                          class="p-2 bg-n-alpha-2 rounded"
                        >
                          <div class="flex items-center justify-between mb-1">
                            <p class="text-xs font-medium">
                              {{ template.title }}
                            </p>
                            <span
                              v-if="template.webhook?.enabled"
                              class="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300"
                            >
                              Webhook
                            </span>
                          </div>
                          <p class="text-[11px] text-n-slate-11 line-clamp-2">
                            {{ template.content }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </span>
                </div>
              </div>

              <!-- Agentes -->
              <div
                v-if="funnel.settings?.agents?.length"
                class="flex flex-col gap-2"
              >
                <p
                  class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
                >
                  {{ t('KANBAN.FUNNELS.CARD.AGENTS_LABEL') }}
                </p>
                <div class="flex flex-wrap gap-2">
                  <div
                    v-for="agent in funnel.settings.agents"
                    :key="agent.id"
                    class="flex items-center gap-2 px-2 py-1 bg-n-solid-2 rounded-xl"
                  >
                    <Avatar
                      :name="agent.name"
                      :src="agent.thumbnail"
                      :size="20"
                    />
                    <span class="text-xs text-n-slate-12">
                      {{ agent.name }}
                    </span>
                  </div>
                </div>
              </div>
              <div
                v-if="(funnelActiveAutomationsCountById[funnel.id] ?? 0) > 0"
                class="inline-flex w-fit shrink-0 items-center gap-1.5 pointer-events-none text-n-slate-11"
                :title="`${t('KANBAN.COLUMN_OPTIONS.STAGE_AUTOMATIONS')} (${funnelActiveAutomationsCountById[funnel.id]})`"
                role="img"
                :aria-label="`${t('KANBAN.COLUMN_OPTIONS.STAGE_AUTOMATIONS')} (${funnelActiveAutomationsCountById[funnel.id]})`"
              >
                <span class="relative inline-flex">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path
                      d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"
                    />
                  </svg>
                  <span
                    class="pointer-events-none absolute -right-0.5 -top-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-[#f59e0b] px-0.5 text-[8px] font-semibold leading-none text-white dark:bg-[#d97706]"
                  >
                    {{
                      (funnelActiveAutomationsCountById[funnel.id] ?? 0) > 99
                        ? '99+'
                        : funnelActiveAutomationsCountById[funnel.id]
                    }}
                  </span>
                </span>
                <span class="text-[11px] font-medium">
                  {{
                    t('KANBAN.FUNNELS.CARD.AUTOMATIONS_CONFIGURED', {
                      count: funnelActiveAutomationsCountById[funnel.id],
                    })
                  }}
                </span>
              </div>

              <!-- Stats do funil -->
              <div v-if="funnel.stats" class="grid grid-cols-2 gap-2 text-xs">
                <div
                  class="flex items-center justify-between gap-2 px-2 py-1.5 bg-n-solid-2/50 rounded"
                >
                  <span class="text-n-slate-11">{{
                    t('KANBAN.FUNNELS.CARD.TOTAL_ITEMS')
                  }}</span>
                  <span class="font-medium text-n-slate-12">{{
                    funnel.stats.total_items ?? 0
                  }}</span>
                </div>
                <div
                  v-if="
                    funnel.stats.total_value != null &&
                    funnel.stats.total_value !== 0
                  "
                  class="flex items-center justify-between gap-2 px-2 py-1.5 bg-n-solid-2/50 rounded col-span-2"
                >
                  <span class="text-n-slate-11">{{
                    t('KANBAN.FUNNELS.CARD.TOTAL_VALUE')
                  }}</span>
                  <span class="font-medium text-n-slate-12">{{
                    formatCurrency(funnel.stats.total_value)
                  }}</span>
                </div>
                <div
                  class="flex items-center justify-between gap-2 px-2 py-1.5 bg-green-50 dark:bg-green-900/20 rounded"
                >
                  <span class="text-green-600 dark:text-green-400">{{
                    t('KANBAN.FUNNELS.CARD.WON_ITEMS')
                  }}</span>
                  <span
                    class="font-medium text-green-700 dark:text-green-300"
                    >{{ funnel.stats.won_items ?? 0 }}</span>
                </div>
                <div
                  class="flex items-center justify-between gap-2 px-2 py-1.5 bg-red-50 dark:bg-red-900/20 rounded"
                >
                  <span class="text-red-600 dark:text-red-400">{{
                    t('KANBAN.FUNNELS.CARD.LOST_ITEMS')
                  }}</span>
                  <span class="font-medium text-red-700 dark:text-red-300">{{
                    funnel.stats.lost_items ?? 0
                  }}</span>
                </div>
                <div
                  class="flex items-center justify-between gap-2 px-2 py-1.5 bg-n-solid-2/50 rounded"
                >
                  <span class="text-n-slate-11">{{
                    t('KANBAN.FUNNELS.CARD.OPEN_ITEMS')
                  }}</span>
                  <span class="font-medium text-n-slate-12">{{
                    funnel.stats.open_items ?? 0
                  }}</span>
                </div>
                <div
                  v-if="(funnel.stats.sla_breached_total ?? 0) > 0"
                  class="flex items-center justify-between gap-2 px-2 py-1.5 bg-amber-50 dark:bg-amber-900/20 rounded"
                >
                  <span class="text-amber-600 dark:text-amber-400">{{
                    t('KANBAN.FUNNELS.CARD.SLA_BREACHED')
                  }}</span>
                  <span
                    class="font-medium text-amber-700 dark:text-amber-300"
                    >{{ funnel.stats.sla_breached_total }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Modal de Confirmação de Exclusão -->
    <Modal
      v-model:show="showDeleteModal"
      :on-close="() => (showDeleteModal = false)"
    >
      <div class="p-6">
        <h3 class="text-lg font-extrabold mb-4">
          {{ t('KANBAN.FUNNELS.DELETE.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 mb-6">
          {{
            t('KANBAN.FUNNELS.DELETE.DESCRIPTION', {
              name: funnelToDelete?.name,
            })
          }}
        </p>
        <div class="flex justify-end gap-2">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            @click="showDeleteModal = false"
          >
            {{ t('KANBAN.CANCEL') }}
          </Button>
          <Button
            variant="solid"
            color="ruby"
            size="sm"
            :is-loading="loading"
            @click="handleDelete"
          >
            {{ t('KANBAN.DELETE') }}
          </Button>
        </div>
      </div>
    </Modal>

    <!-- Modal de Métricas do Funil -->
    <Modal
      v-model:show="showMetricsModal"
      :show-close-button="false"
      :on-close="() => (showMetricsModal = false)"
      class="metrics-modal"
    >
      <div class="w-full">
        <header class="flex items-center justify-between gap-4 px-8 py-3.5">
          <div class="min-w-0 flex-1 py-0.5">
            <h3
              class="text-lg font-extrabold leading-tight text-n-slate-12 dark:text-white"
            >
              Métricas do Funil
            </h3>
            <p class="mt-1 text-sm leading-snug text-n-slate-11 truncate">
              {{ selectedFunnelForMetrics?.name }} • Últimos 30 dias
            </p>
          </div>
          <div
            v-if="selectedFunnelForMetrics"
            class="flex shrink-0 flex-wrap items-center justify-end gap-1"
          >
            <Button
              variant="outline"
              color="slate"
              size="sm"
              :label="t('KANBAN.FUNNELS.CARD.EDIT')"
              @click="openFunnelEditFromMetrics"
            >
              <template #icon>
                <span class="i-ri-pencil-line size-4" />
              </template>
            </Button>
            <Button
              variant="ghost"
              color="slate"
              size="sm"
              @click="duplicateFunnel(selectedFunnelForMetrics)"
            >
              <template #icon>
                <span class="i-ri-file-copy-line size-4" />
              </template>
            </Button>
            <Button
              variant="ghost"
              color="ruby"
              size="sm"
              @click="confirmDelete(selectedFunnelForMetrics)"
            >
              <template #icon>
                <span class="i-ri-delete-bin-line size-4" />
              </template>
            </Button>
          </div>
        </header>

        <div class="px-8 pb-8 pt-6">
          <div
            v-if="metricsLoading"
            class="flex justify-center items-center py-20"
          >
            <span class="loading-spinner" />
          </div>

          <div v-else class="space-y-8">
            <KanbanMetrics
              :metrics="metricsData"
              :format-currency="formatCurrency"
            />

            <div
              v-if="!metricsData.totalItems"
              class="text-center py-10 bg-n-solid-2/50 rounded-2xl border border-dashed border-n-weak"
            >
              <span
                class="i-ri-information-line size-8 mx-auto mb-3 text-n-slate-10"
              />
              <p class="text-n-slate-11">
                Nenhum dado disponível para o período selecionado.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-border-light);
  border-top: 3px solid var(--color-woot);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.funnels-content {
  min-height: 0; // Importante para o scroll funcionar
}

:deep(.funnel-modal),
:deep(.metrics-modal) {
  .modal-container {
    @apply max-w-6xl w-[95vw];
  }
}
</style>
